import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CommonService } from 'wire-frame';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { ApiService } from './api.service';
import {
  ChiefComplaintAssistApiResponse,
  ChiefComplaintAssistRequest,
} from '../../models/chief-complaint-assist.model';

export interface AiStatusResponse {
  success: boolean;
  ai_enabled: boolean;
  features?: {
    consultation_summary?: boolean;
    translate?: boolean;
    chief_complaint_assist?: boolean;
    report_analysis?: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AiService {

  private apiUrl: string;
  private readonly aiEnabledSubject = new BehaviorSubject<boolean>(true);
  /** Latest known AI enabled state (defaults to true until status is loaded). */
  readonly aiEnabled$ = this.aiEnabledSubject.asObservable();

  private statusRequest$: Observable<boolean> | null = null;

  constructor(private http: HttpClient, private common: CommonService, private apiService: ApiService) {
    this.apiUrl = this.apiService.getBaseUrl();
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: new HttpHeaders({
        'Authorization' : `Bearer ${token}`
      })
    }
  }

  /** Current snapshot; prefer aiEnabled$ for reactive UI. */
  get isAiEnabled(): boolean {
    return this.aiEnabledSubject.value;
  }

  /**
   * Fetch AI kill-switch status from the backend (cached per session).
   * On network failure, keeps AI visible so a status outage does not hide features.
   */
  loadStatus(force = false): Observable<boolean> {
    if (force) {
      this.statusRequest$ = null;
    }

    if (!this.statusRequest$) {
      this.statusRequest$ = this.http
        .get<AiStatusResponse>(`${this.apiUrl}/ai/status`, this.getAuthHeaders())
        .pipe(
          map((res) => res?.ai_enabled !== false),
          tap((enabled) => this.aiEnabledSubject.next(enabled)),
          catchError(() => {
            this.aiEnabledSubject.next(true);
            return of(true);
          }),
          shareReplay(1)
        );
    }

    return this.statusRequest$;
  }

  generatePlan(patient: any, prescription: any) {
    return this.http.post(
      this.apiUrl + '/ai/generate',
      { patient, prescription },
      this.getAuthHeaders()
    );
  }

  translateReport(htmlContent: string, language: string) {
    return this.http.post(
      this.apiUrl + '/ai/translate',
      { html: htmlContent, language },
      this.getAuthHeaders()
    );
  }

  chiefComplaintAssist(
    payload: ChiefComplaintAssistRequest
  ): Observable<ChiefComplaintAssistApiResponse> {
    return this.http.post<ChiefComplaintAssistApiResponse>(
      `${this.apiUrl}/ai/chief-complaint-assist`,
      payload,
      this.getAuthHeaders()
    );
  }
}
